import {
  Phone,
  useCurrentAccount,
  useSendAuthCode,
  useWorkspace,
  useTheme,
} from '@gripp/shared-logic';
import {
  createBaseTheme,
  Colors,
  EditProfileForm,
  InvitationAccepted,
  InvitationIntro,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useAcceptInvitationForm,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Dimensions, Modal, StyleSheet, View } from 'react-native';
import { PaperProvider } from 'react-native-paper';
import { useNavigate, useParams } from 'react-router-dom';

export default function AcceptInvitation() {
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invitationId, workspaceId } = useParams();
  const { account, isLoading } = useCurrentAccount();
  const [modalVisible, setModalVisible] = useState(false);
  const [visibleScreen, setVisibleScreen] = useState('intro');
  const { showBoundary } = useErrorBoundary();
  const workspace = useWorkspace(workspaceId as string);
  const { sendAuthCodeAsync } = useSendAuthCode();

  // Only prompt sign-up when the visitor is NOT already logged in. A logged-in
  // account is accepted automatically below, so the modal must never appear.
  useEffect(() => {
    if (!workspace || isLoading || account) return;
    const timer = setTimeout(() => {
      setModalVisible(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [workspace, account, isLoading]);

  const onAccept = async (
    success: boolean,
    phone: Phone | undefined,
    email: string
  ) => {
    const input = {
      phone,
      email,
    };

    await sendAuthCodeAsync(input, () => navigate('/verify-auth-code'));
  };

  const onAuthenticatedAccept = async (success: boolean) => {
    // Surface a failed acceptance instead of routing home as if it worked.
    if (!success) {
      showBoundary(new Error('Failed to accept invitation'));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['currentAccount'] });
    navigate('/');
  };

  const { control, handleSubmit, formState } = useAcceptInvitationForm({
    invitationId: invitationId as string,
    onAccept: account ? onAuthenticatedAccept : onAccept,
    account,
  });

  // Logged in: auto-accept for this account and route home. Keyed on account id
  // (fires once the session account resolves), ref-guarded against double-submit.
  const hasAutoAccepted = useRef(false);
  useEffect(() => {
    if (!account?.id || hasAutoAccepted.current) return;
    hasAutoAccepted.current = true;
    handleSubmit().catch(showBoundary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  return (
    <PaperProvider theme={createBaseTheme(false, resolvedTheme)}>
      {visibleScreen === 'intro' && (
        <InvitationIntro workspace={workspace} t={t} />
      )}
      {visibleScreen === 'accepted' && <InvitationAccepted t={t} />}
      <Modal visible={modalVisible && !account}>
        <View style={styles.modalContainer}>
          <View style={styles.contentContainer}>
            <SaveFormTop
              title={t('invitation.pending.title')}
              saveLabel={t('invitation.pending.join')}
              onSubmit={handleSubmit}
              hasBorder={true}
              onCancel={() => navigate('/login')}
              topBar={TopModalBar}
              isDirty={formState.isDirty}
              isValid={formState.isValid}
            >
              <View style={styles.formContainer}>
                <EditProfileForm
                  control={control}
                  inputMaxWidth={520}
                  autoFocus
                />
              </View>
            </SaveFormTop>
          </View>
        </View>
      </Modal>
    </PaperProvider>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 10,
    backgroundColor: Colors.role.surfaceActive,
  },
  contentContainer: {
    backgroundColor: Colors.role.surfaceHover,
    width: 560,
    borderRadius: 12,
  },
  formContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    height: '100%',
    width: '100%',
  },
});
